/**
 * Workshop WeatherFrame — Apple-Weather-like place forecast.
 *
 * Props-in, JSX-out.
 */

import { cn } from "@/lib/cn";
import { Icons, type IconName } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface WeatherDay {
  day: string;
  icon: IconName;
  high: number;
  low: number;
}

interface WeatherFrameProps {
  place: string;
  temp: number;
  condition: string;
  high: number;
  low: number;
  days?: WeatherDay[];
  status?: FrameStatus;
  className?: string;
  "data-testid"?: string;
}

const MOCK_DAYS: WeatherDay[] = [
  { day: "Today", icon: "Sun", high: 23, low: 14 },
  { day: "Tue", icon: "Sun", high: 22, low: 13 },
  { day: "Wed", icon: "Cloud", high: 19, low: 12 },
  { day: "Thu", icon: "Cloud", high: 18, low: 11 },
  { day: "Fri", icon: "Zap", high: 17, low: 10 },
];

const MAX_VISIBLE_DAYS = 5;

function isIconName(value: unknown): value is IconName {
  return typeof value === "string" && value in Icons;
}

function inferConditionIcon(condition: string): IconName {
  const text = condition.toLowerCase();
  if (/thunder|storm|lightning/.test(text)) return "Zap";
  if (/moon|night/.test(text)) return "Moon";
  if (/sun|clear/.test(text) && !/cloud/.test(text)) return "Sun";
  return "Cloud";
}

export function WeatherFrame({
  place = "Berlin",
  temp = 21,
  condition = "Sunny",
  high = 23,
  low = 14,
  days = MOCK_DAYS,
  status = "done",
  className,
  "data-testid": testId = "weather-frame",
}: WeatherFrameProps) {
  const visibleDays = days.slice(0, MAX_VISIBLE_DAYS);
  const rangeMin = Math.min(low, ...visibleDays.map((d) => d.low));
  const rangeMax = Math.max(high, ...visibleDays.map((d) => d.high));
  const rangeSpan = Math.max(1, rangeMax - rangeMin);

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex flex-col overflow-hidden rounded-frame",
        className,
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-3 py-2.5">
        {status === "error" ? (
          <div
            className="flex flex-col items-center gap-1.5 rounded-preview bg-danger-muted px-3 py-6 text-center"
            data-testid={`${testId}-error`}
          >
            <Icons.CircleAlert className="h-4 w-4 text-status-error" />
            <p className="text-canvas-sm text-secondary">
              Couldn&apos;t load the forecast.
            </p>
            <p className="text-canvas-xs text-muted">
              Check the weather service and try again later.
            </p>
          </div>
        ) : status === "streaming" ? (
          <div data-testid={`${testId}-loading`}>
            <div className="mb-2 flex items-center gap-2">
              <Spinner className="h-3 w-3" />
              <span className="text-canvas-xs text-muted">
                Fetching forecast…
              </span>
            </div>
            <div
              className="h-12 w-24 animate-pulse rounded-preview bg-elevated/60"
              aria-hidden="true"
            />
            <div className="mt-2 flex flex-col gap-1.5" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-7 animate-pulse rounded-control bg-elevated/60"
                />
              ))}
            </div>
          </div>
        ) : visibleDays.length === 0 ? (
          <div
            className="flex flex-1 items-center gap-3 px-1 py-1"
            data-testid={`${testId}-compact`}
          >
            {(() => {
              const CompactIcon = Icons[inferConditionIcon(condition)];
              return (
                <CompactIcon
                  className="h-8 w-8 shrink-0 text-secondary"
                  aria-hidden="true"
                />
              );
            })()}
            <span
              className="shrink-0 text-canvas-xl font-extralight tabular-nums text-primary"
              data-testid={`${testId}-temp`}
            >
              {temp}°
            </span>
            <span className="min-w-0 flex-1">
              <span
                className="block truncate text-canvas-sm font-semibold text-primary"
                data-testid={`${testId}-place`}
              >
                {place}
              </span>
              <span
                className="block truncate text-canvas-xs text-secondary"
                data-testid={`${testId}-condition`}
              >
                {condition}
              </span>
              <span
                className="block text-canvas-2xs text-muted"
                data-testid={`${testId}-high-low`}
              >
                H:{high}°&nbsp;&nbsp;L:{low}°
              </span>
            </span>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-0.5 py-1">
              <span
                className="text-canvas-sm font-semibold text-primary"
                data-testid={`${testId}-place`}
              >
                {place}
              </span>
              <span
                className="text-canvas-xl font-extralight tabular-nums text-primary"
                data-testid={`${testId}-temp`}
              >
                {temp}°
              </span>
              <span
                className="text-canvas-sm text-secondary"
                data-testid={`${testId}-condition`}
              >
                {condition}
              </span>
              <span
                className="text-canvas-xs text-muted"
                data-testid={`${testId}-high-low`}
              >
                H:{high}°&nbsp;&nbsp;L:{low}°
              </span>
            </div>

            <div
              className="h-px shrink-0 bg-glass-stroke-faint"
              aria-hidden="true"
            />
            <div
              className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
              data-testid={`${testId}-days`}
            >
                  {visibleDays.map((day, i) => {
                    const DayIcon = isIconName(day.icon)
                      ? Icons[day.icon]
                      : Icons.Cloud;
                    const left = ((day.low - rangeMin) / rangeSpan) * 100;
                    const width = Math.max(
                      8,
                      ((day.high - day.low) / rangeSpan) * 100,
                    );
                    return (
                      <div
                        key={`${day.day}-${i}`}
                        data-testid={`${testId}-day-${i}`}
                        className="flex items-center gap-2 rounded-control px-2.5 py-1.5 hover:bg-glass-stroke-faint/20"
                      >
                        <span className="w-12 shrink-0 text-canvas-xs font-medium text-secondary">
                          {day.day}
                        </span>
                        <DayIcon
                          className="h-3.5 w-3.5 shrink-0 text-muted"
                          aria-hidden="true"
                        />
                        <span className="w-7 shrink-0 text-right text-canvas-xs tabular-nums text-muted">
                          {day.low}°
                        </span>
                        <span
                          className="relative h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-elevated/70"
                          aria-hidden="true"
                        >
                          <span
                            className="absolute top-0 h-full rounded-full bg-accent/70"
                            style={{
                              left: `${Math.min(92, left)}%`,
                              width: `${Math.min(100, width)}%`,
                            }}
                          />
                        </span>
                        <span className="w-7 shrink-0 text-right text-canvas-xs font-medium tabular-nums text-primary">
                          {day.high}°
                        </span>
                      </div>
                    );
                  })}
                </div>
          </>
        )}
      </div>
    </div>
  );
}

export type { WeatherFrameProps, WeatherDay };
